import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SubscriptionPlanCard from '../components/SubscriptionPlanCard';
import { SUBSCRIPTION_PLANS, SUBSCRIPTION_SERVICE } from '../config/subscription';
import { SubscriptionPlan } from '../types/subscription';
import { useAuth } from '../context/AuthContext';
import { useNotification } from '../context/NotificationContext';
import { subscribePlan } from '../services/subscriptionService';
import { getFullMsisdn } from '../utils/phone';
import { ERROR_MESSAGE, SUCCESS_MESSAGES } from '../constants/messages';
import './Subscribe.css';

const Subscribe: React.FC = () => {
  const navigate = useNavigate();
  const { verifiedPhone, pendingGame, activateSubscription, clearPendingGame } = useAuth();
  const { showSuccess, showError } = useNotification();
  const [loadingPlanId, setLoadingPlanId] = useState<string | null>(null);

  const handleSubscribe = async (plan: SubscriptionPlan) => {
    if (!verifiedPhone) {
      showError();
      return;
    }

    setLoadingPlanId(plan.id);

    try {
      const response = await subscribePlan(
        getFullMsisdn(verifiedPhone),
        plan.subscriptionId
      );

      if (response.ok) {
        activateSubscription(plan, getFullMsisdn(verifiedPhone));
        showSuccess(SUCCESS_MESSAGES.subscribed);

        const gameToPlay = pendingGame;
        clearPendingGame();

        navigate('/', {
          state: gameToPlay ? { playGameId: gameToPlay.id } : undefined,
        });
      } else {
        showError(response.errorMessage || ERROR_MESSAGE);
      }
    } catch {
      showError();
    } finally {
      setLoadingPlanId(null);
    }
  };

  return (
    <div className="subscribe-page">
      <div className="subscribe-header">
        <button
          type="button"
          className="subscribe-back-btn"
          onClick={() => navigate('/')}
          aria-label="Go back"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
        </button>
        <div className="subscribe-header-text">
          <h1 className="subscribe-title">Choose Your Plan</h1>
          <p className="subscribe-subtitle">
            Select a subscription to unlock unlimited gaming
          </p>
          {verifiedPhone && (
            <p className="subscribe-phone">Verified: {verifiedPhone}</p>
          )}
          {pendingGame && (
            <p className="subscribe-game-hint">Ready to play: {pendingGame.title}</p>
          )}
        </div>
      </div>

      <div className="subscribe-plans-grid">
        {SUBSCRIPTION_PLANS.map((plan) => (
          <SubscriptionPlanCard
            key={plan.id}
            plan={plan}
            onSubscribe={handleSubscribe}
            isLoading={loadingPlanId === plan.id}
          />
        ))}
      </div>

      <p className="subscribe-footer-note">
        Charges apply via mobile billing. Short code: {SUBSCRIPTION_SERVICE.shortCode}
      </p>
    </div>
  );
};

export default Subscribe;
